import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button } from "../../../../ui/components/Button";
import { TextField } from "../../../../ui/components/TextField";
import { TextArea } from "../../../../ui/components/TextArea";
import { Select } from "../../../../ui/components/Select";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { StatusBadge } from "../components/StatusBadge";
import { Badge } from "../../../../ui/components/Badge";
import {
  FeatherArrowLeft,
  FeatherFileText,
  FeatherAlertCircle,
  FeatherPlus,
  FeatherX,
  FeatherTrash2,
  FeatherEdit,
} from "@subframe/core";
import { useMonthlyChecklist } from "../hooks/useMonthlyChecklist";
import { ChecklistItemEntity } from "../../domain/entities/MonthlyChecklistEntity";
import { useToast } from "../../../../shared/presentation/components";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { useAuth } from "../../../../features/auth";
import { Loader } from "../../../../ui/components/Loader";
import { Checkbox } from "../../../../ui/components/Checkbox";
import {
  MONTH_END_CHECKLIST_ACCOUNTING_AREA_OPTIONS,
  CHECKLIST_ITEM_PRIORITY_OPTIONS,
  MONTH_END_CHECKLIST_ITEM_WORKFLOW_STATUSES,
  parseMonthEndChecklistItemWorkflowStatus,
  type ChecklistItemPriority,
  type MonthEndChecklistItemWorkflowStatus,
} from "../../../../shared/constants/monthEndChecklist";

const ACCOUNTING_AREAS = MONTH_END_CHECKLIST_ACCOUNTING_AREA_OPTIONS;

const MONTH_ITEM_STATUS_LABELS: Record<MonthEndChecklistItemWorkflowStatus, string> = {
  not_started: "Not started",
  in_progress: "In progress",
  done: "Done",
  submitted_for_review: "Submitted for review",
  approved: "Approved",
  rejected: "Rejected",
  to_be_done_later: "To be done later",
};

export const ChecklistDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { checklistId } = useParams<{ checklistId: string }>();
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const {
    checklist,
    itemsByArea,
    loading,
    loadingMore,
    hasMore,
    fetchChecklist,
    fetchItems,
    loadMoreItems,
    addItem,
    updateItem,
    deleteItem,
  } = useMonthlyChecklist();
  const [selectedArea, setSelectedArea] = useState<string | null>(null);
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ChecklistItemEntity | null>(
    null
  );
  const [deleteItemDialogOpen, setDeleteItemDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<ChecklistItemEntity | null>(
    null
  );
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [scrollObserverRefs, setScrollObserverRefs] = useState<
    Record<string, (node: HTMLDivElement | null) => void>
  >({});

  // Get month_year from location state or checklist
  const monthYear =
    (location.state as any)?.month_year || checklist?.month_year;

  // Check if current month
  const currentMonth = `${new Date().getFullYear()}-${String(
    new Date().getMonth() + 1
  ).padStart(2, "0")}`;
  const isCurrentMonth = monthYear === currentMonth;

  // Check if user is Admin or CFO
  const isAdminOrCFO =
    user?.role?.toLowerCase() === "admin" ||
    user?.role?.toLowerCase() === "cfo";

  const canEditMonthItems = Boolean(
    checklist &&
      isCurrentMonth &&
      isAdminOrCFO &&
      checklist.status !== "closed"
  );

  // Form state for adding item
  const [formData, setFormData] = useState({
    accounting_area: "",
    title: "",
    description: "",
    task_guidance: [""] as string[],
    accepted_proof_types: ["screenshot"],
    order_index: 0,
    priority: "medium" as ChecklistItemPriority,
    requires_proof: true,
    status: "not_started" as MonthEndChecklistItemWorkflowStatus,
  });

  useEffect(() => {
    if (!checklistId) return;

    const loadData = async () => {
      try {
        await fetchChecklist(parseInt(checklistId));
        await fetchItems(parseInt(checklistId));
      } catch (err) {
        showError("Failed to load checklist");
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checklistId]);

  // Set default selected area when items load
  useEffect(() => {
    if (!selectedArea && Object.keys(itemsByArea).length > 0) {
      const firstArea = Object.keys(itemsByArea).sort()[0];
      setSelectedArea(firstArea);
    }
  }, [itemsByArea, selectedArea]);

  // Set up infinite scroll observer for the selected area
  useEffect(() => {
    if (!checklistId || !selectedArea) return;

    let observer: IntersectionObserver | null = null;
    const ref = (node: HTMLDivElement | null) => {
      // Clean up previous observer
      if (observer) {
        observer.disconnect();
        observer = null;
      }

      if (node && hasMore[selectedArea] && !loadingMore[selectedArea]) {
        observer = new IntersectionObserver(
          (entries) => {
            if (
              entries[0].isIntersecting &&
              !loadingMore[selectedArea] &&
              hasMore[selectedArea]
            ) {
              loadMoreItems(parseInt(checklistId), selectedArea);
            }
          },
          { threshold: 0.1 }
        );
        observer.observe(node);
      }
    };

    setScrollObserverRefs((prev) => ({
      ...prev,
      [selectedArea]: ref,
    }));

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [selectedArea, hasMore, loadingMore, checklistId, loadMoreItems]);

  const formatMonthYear = (monthYear: string | undefined): string => {
    if (!monthYear) {
      return "Unknown Month";
    }
    const [year, month] = monthYear.split("-");
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  };

  const getStatusBadge = (status: string) => {
    return <StatusBadge status={status as any} />;
  };

  // Helper to safely check if item is deleted (handles edge cases where value might be string "0"/"1")
  const isItemDeleted = (item: ChecklistItemEntity): boolean => {
    if (item.is_deleted === undefined || item.is_deleted === null) {
      return false;
    }
    if (typeof item.is_deleted === "boolean") {
      return item.is_deleted;
    }
    // Defensive check: if somehow it's still a string, normalize it
    if (typeof item.is_deleted === "string") {
      const normalized = item.is_deleted;
      return normalized === "1" || normalized === "true";
    }
    // If it's a number
    if (typeof item.is_deleted === "number") {
      return item.is_deleted === 1;
    }
    return false;
  };

  // Get areas that have items
  const areasWithItems = Object.keys(itemsByArea).sort();

  // Treat approved and done as completed (backend uses these; completed is also used)
  const isItemCompleted = (status: string) =>
    status === "completed" ||
    status === "done" ||
    status === "approved" ||
    status === "to_be_done_later";

  // Get progress for each area
  const getAreaProgress = (area: string) => {
    const areaItems = itemsByArea[area] || [];
    const total = areaItems.length;
    const completed = areaItems.filter((item: ChecklistItemEntity) =>
      isItemCompleted(item.status)
    ).length;
    return { total, completed };
  };

  const itemStatusForForm = (
    item: ChecklistItemEntity
  ): MonthEndChecklistItemWorkflowStatus => {
    if (item.status === "completed") {
      return "done";
    }
    return parseMonthEndChecklistItemWorkflowStatus(item.status);
  };

  const resetAddForm = () => {
    const currentAreaItems = selectedArea
      ? itemsByArea[selectedArea] || []
      : [];
    setFormData({
      accounting_area: selectedArea || "",
      title: "",
      description: "",
      task_guidance: [""],
      accepted_proof_types: ["screenshot"],
      order_index: currentAreaItems.length + 1,
      priority: "medium",
      requires_proof: true,
      status: "not_started",
    });
    setEditingItem(null);
  };

  const handleAddItem = () => {
    if (!selectedArea) {
      showError("Please select an accounting area first");
      return;
    }
    const currentAreaItems = itemsByArea[selectedArea] || [];
    setEditingItem(null);
    setFormData({
      accounting_area: selectedArea,
      title: "",
      description: "",
      task_guidance: [""],
      accepted_proof_types: ["screenshot"],
      order_index: currentAreaItems.length + 1,
      priority: "medium",
      requires_proof: true,
      status: "not_started",
    });
    setAddItemDialogOpen(true);
  };

  const handleEditItem = (item: ChecklistItemEntity) => {
    setEditingItem(item);
    setFormData({
      accounting_area: item.accounting_area,
      title: item.title,
      description: item.description ?? "",
      task_guidance:
        item.task_guidance && item.task_guidance.length > 0
          ? [...item.task_guidance]
          : [""],
      accepted_proof_types:
        item.accepted_proof_types && item.accepted_proof_types.length > 0
          ? [...item.accepted_proof_types]
          : ["screenshot"],
      order_index: item.order_index,
      priority: (item.priority as ChecklistItemPriority) || "medium",
      requires_proof: item.requires_proof !== false,
      status: itemStatusForForm(item),
    });
    setAddItemDialogOpen(true);
  };

  const handleSaveItem = async () => {
    if (!formData.accounting_area || !formData.title || !formData.description?.trim() || !checklistId) {
      showError("Accounting area, title, and description are required");
      return;
    }

    const cid = parseInt(checklistId, 10);
    const guidance = formData.task_guidance.filter((step) => step.trim() !== "");

    try {
      setSaving(true);
      if (editingItem) {
        await updateItem(cid, editingItem.id, {
          accounting_area: formData.accounting_area,
          title: formData.title,
          description: formData.description,
          task_guidance: guidance,
          accepted_proof_types: formData.accepted_proof_types,
          requires_proof: formData.requires_proof,
          order_index: formData.order_index,
          priority: formData.priority,
          status: formData.status,
        });
        showSuccess("Item updated successfully");
      } else {
        await addItem(cid, {
          accounting_area: formData.accounting_area,
          title: formData.title,
          description: formData.description,
          task_guidance: guidance,
          accepted_proof_types: formData.accepted_proof_types,
          requires_proof: formData.requires_proof,
          order_index: formData.order_index,
          priority: formData.priority,
          link_url: undefined,
          link_text: undefined,
          system_info_query: undefined,
          status: formData.status,
        });
        showSuccess("Item added successfully");
      }

      setAddItemDialogOpen(false);
      resetAddForm();
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to save item";
      showError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddTaskStep = () => {
    setFormData({
      ...formData,
      task_guidance: [...formData.task_guidance, ""],
    });
  };

  const handleRemoveTaskStep = (index: number) => {
    setFormData({
      ...formData,
      task_guidance: formData.task_guidance.filter((_, i) => i !== index),
    });
  };

  const handleTaskStepChange = (index: number, value: string) => {
    const newSteps = [...formData.task_guidance];
    newSteps[index] = value;
    setFormData({
      ...formData,
      task_guidance: newSteps,
    });
  };

  const handleDeleteClick = (item: ChecklistItemEntity) => {
    setItemToDelete(item);
    setDeleteItemDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete || !checklistId) return;

    try {
      setDeleting(true);
      await deleteItem(itemToDelete.id, itemToDelete.accounting_area);
      showSuccess("Item deleted successfully");
      setDeleteItemDialogOpen(false);
      setItemToDelete(null);
    } catch (error: any) {
      showError(error.message || "Failed to delete item");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    if (!deleting) {
      setDeleteItemDialogOpen(false);
      setItemToDelete(null);
    }
  };

  // Show loader while loading checklist or items
  // Only show loader if we're actively loading or don't have checklist yet
  const isLoading = loading || !checklist;

  if (isLoading) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full h-full items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Loader size="large" />
            <span className="text-heading-2 font-heading-2 text-default-font">
              Loading checklist...
            </span>
            <span className="text-body font-body text-subtext-color">
              Please wait while we fetch the checklist and items
            </span>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  if (!checklist) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full h-full flex-col items-center justify-center gap-4 p-12">
          <FeatherAlertCircle className="w-12 h-12 text-subtext-color" />
          <span className="text-heading-3 font-heading-3 text-default-font">
            Checklist not found
          </span>
          <Button
            variant="neutral-secondary"
            onClick={() => navigate("/admin/month-end-checklists")}
          >
            Back to Checklists
          </Button>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="flex w-full h-full flex-col items-start">
        {/* Sticky Header */}
        <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-neutral-border bg-default-background p-6 sticky top-0 z-[5]">
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="neutral-secondary"
                icon={<FeatherArrowLeft />}
                onClick={() => navigate("/admin/month-end-checklists")}
              >
                Back
              </Button>
              <div className="flex flex-col items-start gap-1">
                <span className="text-heading-2 font-heading-2 text-default-font">
                  {formatMonthYear(monthYear)}
                </span>
                <span className="text-body font-body text-subtext-color">
                  {checklist.template_name || "Month End Checklist"}
                </span>
              </div>
            </div>
          </div>

          {/* Overall Progress - from API progress (done/completed) or derived from items */}
          {(() => {
            const p = checklist.progress as { total?: number; completed?: number; done?: number } | undefined;
            let completed: number;
            let total: number;
            if (p && (p.total !== undefined || p.done !== undefined || p.completed !== undefined)) {
              completed = p.completed ?? p.done ?? 0;
              total = p.total ?? 0;
            } else {
              const allItems = Object.values(itemsByArea).flat();
              total = allItems.length;
              completed = allItems.filter((item) => isItemCompleted(item.status)).length;
            }
            return (
              <div className="flex items-center gap-4 w-full">
                <div className="flex items-center gap-2">
                  <span className="text-body font-body text-subtext-color">
                    Overall Progress:
                  </span>
                  <span className="text-body font-body text-default-font font-semibold">
                    {completed} / {total} tasks
                  </span>
                </div>
                <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 transition-all"
                    style={{
                      width: `${total > 0 ? (completed / total) * 100 : 0}%`,
                    }}
                  />
                </div>
              </div>
            );
            })()}
        </div>

        {checklist.status === "closed" && (
          <div className="w-full px-6 pb-2">
            <div className="flex items-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 px-4 py-3 text-body font-body text-default-font">
              <FeatherAlertCircle className="w-5 h-5 shrink-0 text-subtext-color" />
              <span>
                This month is closed. Add, edit, and delete on checklist items
                are disabled.
              </span>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex w-full grow shrink-0 basis-0 overflow-hidden">
          {/* Sidebar - Accounting Areas */}
          <div className="w-64 border-r border-solid border-neutral-border bg-neutral-50 overflow-y-auto">
            <div className="p-4">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Accounting Areas
              </span>
            </div>
            <div className="flex flex-col">
              {areasWithItems.map((area) => {
                const progress = getAreaProgress(area);
                return (
                  <button
                    key={area}
                    onClick={() => setSelectedArea(area)}
                    className={`flex flex-col gap-1 p-4 text-left border-b border-solid border-neutral-border transition-colors ${
                      selectedArea === area
                        ? "bg-default-background border-l-2 border-l-brand-primary"
                        : "hover:bg-neutral-100"
                    }`}
                  >
                    <span className="text-body font-body text-default-font font-semibold">
                      {area}
                    </span>
                    <span className="text-xs text-body font-body text-subtext-color">
                      {progress.completed} / {progress.total} tasks
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Main Content Area - Tasks by Accounting Area */}
          <div className="flex-1 overflow-y-auto bg-default-background">
            {selectedArea && itemsByArea[selectedArea] ? (
              <div className="p-6">
                <div className="mb-6 flex items-center justify-between">
                  <div>
                    <h2 className="text-heading-2 font-heading-2 text-default-font mb-2">
                      {selectedArea}
                    </h2>
                    <p className="text-body font-body text-subtext-color">
                      {itemsByArea[selectedArea].length} task
                      {itemsByArea[selectedArea].length !== 1 ? "s" : ""} in
                      this area
                    </p>
                  </div>
                  {canEditMonthItems && (
                    <Button
                      variant="brand-primary"
                      icon={<FeatherPlus />}
                      onClick={handleAddItem}
                    >
                      Add Item
                    </Button>
                  )}
                </div>

                <div className="flex flex-col gap-4">
                  {itemsByArea[selectedArea]
                    .sort(
                      (a: ChecklistItemEntity, b: ChecklistItemEntity) =>
                        a.order_index - b.order_index
                    )
                    .map((item: ChecklistItemEntity) => (
                      <div
                        key={item.id}
                        className={`flex flex-col gap-4 p-6 border border-solid border-neutral-border rounded-lg ${
                          isItemDeleted(item)
                            ? "opacity-60 bg-neutral-50"
                            : "bg-default-background"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex flex-col gap-2 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-heading-3 font-heading-3 text-default-font">
                                {item.title}
                              </span>
                              {getStatusBadge(
                                (item.status === "completed"
                                  ? "done"
                                  : item.status) as React.ComponentProps<
                                  typeof StatusBadge
                                >["status"]
                              )}
                              {isItemDeleted(item) && (
                                <Badge
                                  variant="neutral"
                                  className="text-xs bg-error-100 text-error-700 border-error-300"
                                  title="This item was deleted from the template and will no longer be used for future monthly checklists"
                                >
                                  Deleted from Template
                                </Badge>
                              )}
                            </div>
                            {item.description && (
                              <p className="text-body font-body text-subtext-color">
                                {item.description}
                              </p>
                            )}
                          </div>
                          {canEditMonthItems && (
                            <div className="flex items-center gap-1 shrink-0">
                              <Button
                                variant="neutral-tertiary"
                                icon={<FeatherEdit />}
                                onClick={() => handleEditItem(item)}
                                title="Edit item"
                              />
                              <Button
                                variant="neutral-tertiary"
                                icon={<FeatherTrash2 />}
                                onClick={() => handleDeleteClick(item)}
                                className="text-error-600 hover:text-error-700"
                                title="Delete item"
                              />
                            </div>
                          )}
                        </div>

                        {/* Task Guidance */}
                        {Array.isArray(item.task_guidance) &&
                          item.task_guidance.length > 0 && (
                            <div className="flex flex-col gap-2">
                              <span className="text-body font-body text-default-font font-semibold">
                                Task Guidance:
                              </span>
                              <ul className="list-disc list-inside flex flex-col gap-1 ml-4">
                                {item.task_guidance.map(
                                  (step: string, index: number) => (
                                    <li
                                      key={index}
                                      className="text-body font-body text-subtext-color"
                                    >
                                      {step}
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>
                          )}

                        {/* Accepted Proof Types */}
                        {Array.isArray(item.accepted_proof_types) &&
                          item.accepted_proof_types.length > 0 && (
                            <div className="flex items-center gap-2">
                              <FeatherFileText className="w-4 h-4 text-subtext-color" />
                              <span className="text-sm text-body font-body text-subtext-color">
                                Accepted Proof:{" "}
                                {item.accepted_proof_types.join(", ")}
                              </span>
                            </div>
                          )}

                        {/* Link */}
                        {item.link_url && (
                          <div>
                            <a
                              href={item.link_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-body font-body text-brand-primary hover:underline"
                            >
                              {item.link_text || "Go to Task"} →
                            </a>
                          </div>
                        )}

                        {/* Notes */}
                        {item.notes && (
                          <div className="p-3 bg-neutral-50 rounded border border-neutral-border">
                            <span className="text-sm text-body font-body text-subtext-color font-semibold">
                              Notes:
                            </span>
                            <p className="text-sm text-body font-body text-default-font mt-1">
                              {item.notes}
                            </p>
                          </div>
                        )}

                        {/* Completed Info - approved/done count as completed */}
                        {isItemCompleted(item.status) && item.completed_at && (
                          <div className="text-xs text-body font-body text-subtext-color">
                            Completed on:{" "}
                            {new Date(item.completed_at).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  {/* Infinite scroll trigger */}
                  {hasMore[selectedArea] && (
                    <div
                      ref={scrollObserverRefs[selectedArea]}
                      className="flex items-center justify-center p-4"
                    >
                      {loadingMore[selectedArea] ? (
                        <span className="text-body font-body text-subtext-color">
                          Loading more items...
                        </span>
                      ) : (
                        <div className="h-4" /> // Spacer for intersection observer
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-4 p-12">
                  <FeatherAlertCircle className="w-12 h-12 text-subtext-color" />
                  <span className="text-body font-body text-subtext-color">
                    Select an accounting area to view tasks
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Item Dialog */}
      <DialogLayout
        open={addItemDialogOpen}
        onOpenChange={(open) => {
          if (!open && !saving) {
            setAddItemDialogOpen(false);
            resetAddForm();
          }
        }}
        className="max-w-[1200px] w-[90vw]"
      >
        <div className="flex w-full flex-col items-start gap-6 bg-default-background px-8 py-6">
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-2 font-heading-2 text-default-font">
              {editingItem ? "Edit Checklist Item" : "Add Checklist Item"}
            </span>
            <Button
              variant="neutral-tertiary"
              icon={<FeatherX />}
              onClick={() => {
                if (!saving) {
                  setAddItemDialogOpen(false);
                  resetAddForm();
                }
              }}
            />
          </div>
          <div className="flex flex-col gap-4 w-full">
            <Select
              label="Accounting Area"
              value={formData.accounting_area}
              onValueChange={(value: string) =>
                setFormData({ ...formData, accounting_area: value || "" })
              }
              placeholder="Select Accounting Area"
            >
              {ACCOUNTING_AREAS.map((area) => (
                <Select.Item key={area.value} value={area.value}>
                  {area.label}
                </Select.Item>
              ))}
            </Select>

            <Select
              label="Priority"
              value={formData.priority}
              onValueChange={(value: string) => {
                const v = value as ChecklistItemPriority;
                if (v === "high" || v === "medium" || v === "low") {
                  setFormData({ ...formData, priority: v });
                }
              }}
              placeholder="Select priority"
            >
              {CHECKLIST_ITEM_PRIORITY_OPTIONS.map((p) => (
                <Select.Item key={p.value} value={p.value}>
                  {p.label}
                </Select.Item>
              ))}
            </Select>

            <Select
              label="Status"
              value={formData.status}
              onValueChange={(value: string) => {
                const v = parseMonthEndChecklistItemWorkflowStatus(value);
                setFormData({ ...formData, status: v });
              }}
              placeholder="Select status"
            >
              {MONTH_END_CHECKLIST_ITEM_WORKFLOW_STATUSES.map((s) => (
                <Select.Item key={s} value={s}>
                  {MONTH_ITEM_STATUS_LABELS[s]}
                </Select.Item>
              ))}
            </Select>

            <Checkbox
              label="Requires proof before marking done"
              checked={formData.requires_proof}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, requires_proof: Boolean(checked) })
              }
            />

            <TextField label="Title">
              <TextField.Input
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="Enter item title"
              />
            </TextField>

            <TextArea label="Description *">
              <TextArea.Input
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Enter item description"
              />
            </TextArea>

            <div className="flex flex-col gap-2">
              <span className="text-caption-bold font-caption-bold text-default-font">
                Task Guidance (Steps)
              </span>
              {formData.task_guidance.map((step, index) => (
                <div key={index} className="flex items-center gap-2">
                  <TextField className="flex-1">
                    <TextField.Input
                      value={step}
                      onChange={(e) =>
                        handleTaskStepChange(index, e.target.value)
                      }
                      placeholder={`Step ${index + 1}`}
                    />
                  </TextField>
                  {formData.task_guidance.length > 1 && (
                    <Button
                      variant="neutral-tertiary"
                      icon={<FeatherX />}
                      onClick={() => handleRemoveTaskStep(index)}
                    />
                  )}
                </div>
              ))}
              <Button
                variant="neutral-secondary"
                onClick={handleAddTaskStep}
                icon={<FeatherPlus />}
              >
                Add Step
              </Button>
            </div>

            <TextField label="Display Order">
              <TextField.Input
                type="number"
                value={formData.order_index.toString()}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    order_index: parseInt(e.target.value) || 0,
                  })
                }
              />
            </TextField>

            <div className="flex items-center justify-end gap-2 pt-4">
              <Button
                variant="neutral-secondary"
                onClick={() => {
                  if (!saving) {
                    setAddItemDialogOpen(false);
                    resetAddForm();
                  }
                }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="brand-primary"
                onClick={handleSaveItem}
                disabled={saving}
              >
                {saving
                  ? editingItem
                    ? "Saving..."
                    : "Adding..."
                  : editingItem
                    ? "Save changes"
                    : "Add Item"}
              </Button>
            </div>
          </div>
        </div>
      </DialogLayout>

      {/* Delete Item Confirmation Dialog */}
      <DialogLayout
        open={deleteItemDialogOpen}
        onOpenChange={(open) => {
          if (!open && !deleting) {
            setDeleteItemDialogOpen(false);
            setItemToDelete(null);
          }
        }}
        className="max-w-[500px] w-[90vw]"
      >
        <div className="flex w-full flex-col items-start gap-6 bg-default-background px-8 py-6">
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Delete Checklist Item
            </span>
            <Button
              variant="neutral-tertiary"
              icon={<FeatherX />}
              onClick={handleDeleteCancel}
              disabled={deleting}
            />
          </div>
          <div className="flex flex-col gap-4 w-full">
            <div className="flex items-start gap-3">
              <FeatherAlertCircle className="w-5 h-5 text-error-600 mt-0.5" />
              <div className="flex flex-col gap-2 flex-1">
                <span className="text-body font-body text-default-font">
                  Are you sure you want to delete this checklist item?
                </span>
                {itemToDelete && (
                  <span className="text-body font-body text-subtext-color">
                    This will delete "{itemToDelete.title}" from this month's
                    checklist only.
                  </span>
                )}
              </div>
            </div>
            <div className="p-4 bg-error-50 border border-error-200 rounded-lg">
              <span className="text-body font-body text-error-700 font-semibold">
                ⚠️ This line item will be deleted for this month checklist only
                !!
              </span>
            </div>
            <div className="flex items-center justify-end gap-2 pt-4">
              <Button
                variant="neutral-secondary"
                onClick={handleDeleteCancel}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive-primary"
                icon={<FeatherTrash2 />}
                onClick={handleDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      </DialogLayout>
    </DefaultPageLayout>
  );
};
